/**
 * One row of the loop, with the four settings the manifest actually supports.
 *
 * Reordering works two ways on purpose. Dragging is what most people reach for;
 * the Up and Down buttons are what everyone else needs, and they are not a
 * fallback — a reorder that only works with a mouse is unusable for anyone
 * driving this from a keyboard, which includes anyone using a screen reader and
 * anyone whose trackpad has given up. Both paths call the same `moveItem`, so
 * they cannot drift.
 *
 * The move buttons stay focusable at the ends of the list rather than going
 * disabled. A button that disables itself under the pointer throws keyboard focus
 * back to the top of the document, which loses your place in a twenty-item
 * playlist; `aria-disabled` says the same thing without doing that, and the press
 * is announced instead.
 */

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type { PlaylistItem } from '@pbtv/shared';
import type { MediaAsset } from '../../lib/media.js';
import { formatBytes } from '../../lib/format.js';
import {
  FIT_CHOICES,
  describeItemLength,
  formatRuntime,
  isVideoMediaType,
} from './runtime.js';
import type { DraftItem } from './runtime.js';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export interface PlaylistItemRowProps {
  row: DraftItem;
  index: number;
  total: number;
  asset: MediaAsset | undefined;
  readOnly: boolean;
  isDragging: boolean;
  isDropTarget: boolean;
  onPatch: (patch: Partial<PlaylistItem>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnterRow: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}

export function PlaylistItemRow({
  row,
  index,
  total,
  asset,
  readOnly,
  isDragging,
  isDropTarget,
  onPatch,
  onMove,
  onRemove,
  onDragStart,
  onDragEnterRow,
  onDragEnd,
  onDrop,
}: PlaylistItemRowProps): ReactElement {
  const item = row.item;
  const video = asset !== undefined && isVideoMediaType(asset.type);

  const classes = ['playlist-row'];
  if (isDragging) classes.push('is-dragging');
  if (isDropTarget) classes.push('is-drop-target');
  if (asset === undefined) classes.push('is-missing');

  return (
    <tr
      className={classes.join(' ')}
      draggable={!readOnly}
      onDragStart={(event) => {
        // Firefox refuses to start a drag without payload on the transfer.
        event.dataTransfer.setData('text/plain', String(index));
        event.dataTransfer.effectAllowed = 'move';
        onDragStart();
      }}
      onDragEnter={onDragEnterRow}
      onDragOver={(event) => {
        if (readOnly) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
    >
      <th scope="row" className="playlist-row__asset">
        <span className="playlist-row__position" aria-hidden="true">
          {index + 1}
        </span>
        <span className="playlist-row__thumb">
          {asset === undefined || asset.thumbUrl === null ? (
            <span className="playlist-row__placeholder" aria-hidden="true">
              {video ? 'MP4' : 'IMG'}
            </span>
          ) : (
            <img src={asset.thumbUrl} alt="" draggable={false} />
          )}
        </span>
        <span className="playlist-row__names">
          <span className="data-table__primary">
            {asset === undefined ? 'Missing asset' : asset.title || asset.assetId}
          </span>
          <span className="data-table__secondary">
            {asset === undefined
              ? `${item.assetId} is not in this playlist's library`
              : `${asset.width} × ${asset.height} · ${formatBytes(asset.bytes)}`}
          </span>
        </span>
      </th>

      <td>
        {video ? (
          <div className="playlist-row__stack">
            <NumberField
              label="Play through"
              suffix="times"
              value={item.repeatCount ?? 1}
              min={1}
              max={100}
              disabled={readOnly}
              onCommit={(repeatCount) => onPatch({ repeatCount })}
            />
            <label className="checkbox">
              <input
                type="checkbox"
                checked={item.muted}
                disabled={readOnly}
                onChange={(event) => onPatch({ muted: event.currentTarget.checked })}
              />
              <span>Muted</span>
            </label>
            <span className="playlist-row__hint">
              {asset === undefined || asset.durationMs === null
                ? 'Natural length unknown'
                : `Natural length ${formatRuntime(asset.durationMs)}`}
            </span>
          </div>
        ) : (
          <NumberField
            label="On screen"
            suffix="seconds"
            value={item.durationSec ?? 8}
            min={1}
            max={3600}
            disabled={readOnly}
            onCommit={(durationSec) => onPatch({ durationSec })}
          />
        )}
      </td>

      <td>
        <label className="field field--tight">
          <span className="visually-hidden">How it fills the screen</span>
          <select
            className="input"
            value={item.fit}
            disabled={readOnly}
            onChange={(event) =>
              onPatch({ fit: event.currentTarget.value as PlaylistItem['fit'] })
            }
          >
            {FIT_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        <span className="playlist-row__hint">
          {FIT_CHOICES.find((choice) => choice.value === item.fit)?.hint ?? ''}
        </span>
      </td>

      <td>
        <ColorField
          value={item.backgroundColor}
          disabled={readOnly}
          onCommit={(backgroundColor) => onPatch({ backgroundColor })}
        />
        <span className="playlist-row__hint">
          {item.fit === 'contain'
            ? 'Fills whatever the picture does not.'
            : 'Nothing is left over at this fit, so it will not be seen.'}
        </span>
      </td>

      <td className="playlist-row__length">{describeItemLength(item, asset)}</td>

      <td className="data-table__actions">
        <MoveButton
          label="Move up"
          disabled={readOnly}
          atEnd={index === 0}
          onActivate={() => onMove(index - 1)}
        />
        <MoveButton
          label="Move down"
          disabled={readOnly}
          atEnd={index === total - 1}
          onActivate={() => onMove(index + 1)}
        />
        <button
          type="button"
          className="button button--ghost button--small"
          disabled={readOnly}
          onClick={onRemove}
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

function MoveButton({
  label,
  disabled,
  atEnd,
  onActivate,
}: {
  label: string;
  disabled: boolean;
  atEnd: boolean;
  onActivate: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      className={atEnd ? 'button button--ghost button--small is-inert' : 'button button--ghost button--small'}
      disabled={disabled}
      aria-disabled={atEnd}
      onClick={() => {
        if (!atEnd) onActivate();
      }}
    >
      {label}
    </button>
  );
}

interface NumberFieldProps {
  label: string;
  suffix: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onCommit: (value: number) => void;
}

/**
 * A number input that lets somebody clear the field and retype it. A directly
 * controlled numeric input cannot: the moment the box is empty the value is NaN,
 * and whatever the parent does with that is wrong. So the text is local, and only
 * a value inside the allowed range is handed upward.
 */
function NumberField({
  label,
  suffix,
  value,
  min,
  max,
  disabled,
  onCommit,
}: NumberFieldProps): ReactElement {
  const [raw, setRaw] = useState(() => String(value));

  useEffect(() => {
    setRaw(String(value));
  }, [value]);

  return (
    <label className="field field--tight">
      <span className="visually-hidden">{label}</span>
      <span className="number-field">
        <input
          type="number"
          className="input input--number"
          value={raw}
          min={min}
          max={max}
          step={1}
          disabled={disabled}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setRaw(next);
            const parsed = Number(next);
            if (next.trim() === '' || !Number.isFinite(parsed)) return;
            if (parsed < min || parsed > max) return;
            onCommit(Math.round(parsed));
          }}
          onBlur={() => setRaw(String(value))}
        />
        <span className="number-field__suffix">{suffix}</span>
      </span>
    </label>
  );
}

/**
 * The letterbox colour, as a swatch and as the hex the manifest carries. The text
 * field exists because brand colours arrive as hex codes in an email, and picking
 * `#b8352a` out of a colour wheel by eye is not a thing anybody should be asked
 * to do.
 */
function ColorField({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (value: string) => void;
}): ReactElement {
  const [raw, setRaw] = useState(() => value);

  useEffect(() => {
    setRaw(value);
  }, [value]);

  const swatch: CSSProperties = { background: HEX_COLOR.test(raw) ? raw : value };

  return (
    <span className="color-field">
      <span className="color-field__swatch" style={swatch} aria-hidden="true" />
      <input
        type="color"
        className="color-field__picker"
        value={HEX_COLOR.test(raw) ? raw : value}
        disabled={disabled}
        aria-label="Letterbox colour"
        onChange={(event) => onCommit(event.currentTarget.value)}
      />
      <input
        type="text"
        className="input input--hex"
        value={raw}
        spellCheck={false}
        disabled={disabled}
        aria-label="Letterbox colour as hex"
        onChange={(event) => {
          const next = event.currentTarget.value;
          setRaw(next);
          if (HEX_COLOR.test(next)) onCommit(next.toLowerCase());
        }}
        onBlur={() => setRaw(value)}
      />
    </span>
  );
}
